import { z } from 'zod';
import type { UserLevel } from '../enums';

export const loginSchema = z.object({
  loginId: z.string().trim().min(1, 'User name is required').max(30),
  password: z.string().min(1, 'Password is required').max(200),
  /** Set after the user chooses "Logout from Other Device". */
  takeover: z.boolean().optional().default(false),
});
export type LoginInput = z.input<typeof loginSchema>;

export interface SessionUser {
  id: string;
  loginId: string;
  name: string;
  level: UserLevel;
  groupId: string;
  groupName: string;
  /** A 212Ez support session (platform console > Open company): who opened it, in which company. */
  support?: { by: string; company: string } | null;
}

export interface MeResponse {
  user: SessionUser;
  permissions: string[];
  /** Minutes of inactivity before the session ends. */
  idleMinutes: number;
  appName: string;
}

/**
 * "Reset Password" in the user menu: the signed-in user changes their own password. The new one
 * follows this application's password rule (the reference's was not observable).
 */
const passwordChange = z.object({
  currentPassword: z.string().min(1, 'Enter your current password').max(200),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(200)
    .regex(/[A-Za-z]/, 'Password must contain a letter')
    .regex(/\d/, 'Password must contain a digit'),
  confirmPassword: z.string().max(200),
});
const checkPasswordChange = (v: z.output<typeof passwordChange>, ctx: z.RefinementCtx): void => {
  if (v.confirmPassword !== v.newPassword)
    ctx.addIssue({
      code: 'custom',
      path: ['confirmPassword'],
      message: 'The passwords do not match',
    });
  if (v.newPassword && v.newPassword === v.currentPassword)
    ctx.addIssue({
      code: 'custom',
      path: ['newPassword'],
      message: 'Choose a password you have not used now',
    });
};
export const changePasswordSchema = passwordChange.superRefine(checkPasswordChange);
export type ChangePasswordInput = z.input<typeof changePasswordSchema>;

/**
 * A password that has expired (Login Password Expiry Days, SC99) is renewed before signing in:
 * the user name, the current password and the new one twice.
 */
export const expiredPasswordSchema = passwordChange
  .extend({ loginId: z.string().trim().min(1, 'User name is required').max(30) })
  .superRefine(checkPasswordChange);
export type ExpiredPasswordInput = z.input<typeof expiredPasswordSchema>;

/**
 * "Forgot your password?" on the sign-in page (release switch `forgot-password`). The reference
 * shows the link; its page was not seen, so the form and the e-mailed link are 212Ez's own.
 */
export const forgotPasswordSchema = z.object({
  loginId: z.string().trim().min(1, 'User name is required').max(30),
});
export type ForgotPasswordInput = z.input<typeof forgotPasswordSchema>;

/** The page the e-mailed link opens: the new password twice. */
export const resetPasswordSchema = z
  .object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'This link is not valid.'),
    newPassword: passwordChange.shape.newPassword,
    confirmPassword: z.string().max(200),
  })
  .superRefine((v, ctx) => {
    if (v.confirmPassword !== v.newPassword)
      ctx.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: 'The passwords do not match',
      });
  });
export type ResetPasswordInput = z.input<typeof resetPasswordSchema>;

/** What "Forgot your password?" always answers, so it never tells whether a user name exists. */
export const FORGOT_PASSWORD_SENT =
  'If this user name has an e-mail address, a link to choose a new password has been sent to it. The link works for 30 minutes.';
